/**
 * ScorePanel — interactive score for both teams.
 *
 * In LIVE mode the +/- icons are clickable. In VIDEO mode they're disabled
 * and the +1 / +2 / +3 opponent quick-buttons can be hidden via the
 * `showOpponentQuickButtons` prop.
 *
 * Pulled from the score block in renderLiveMode() of EstadisticasNew.js.
 */

import React from 'react';
import { Box, Typography, IconButton, ButtonBase } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useStatsTheme, withAlpha } from '../theme/statsTheme';

function ScoreBlock({ label, value, color, onIncrement, onDecrement, disabled }) {
  const theme = useStatsTheme();
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          color,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
        <IconButton
          size="small"
          onClick={onDecrement}
          disabled={disabled}
          aria-label="decrement score"
          sx={{
            width: 32,
            height: 32,
            backgroundColor: withAlpha(theme.actionColors.missed, 0.18),
            border: `1px solid ${withAlpha(theme.actionColors.missed, 0.4)}`,
            color: theme.actionColors.missed,
            '&:hover': { backgroundColor: withAlpha(theme.actionColors.missed, 0.3) },
            '&.Mui-disabled': { opacity: 0.3 },
          }}
        >
          <RemoveIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Box
          sx={{
            flex: 1,
            textAlign: 'center',
            backgroundColor: withAlpha(color, 0.18),
            border: `1px solid ${withAlpha(color, 0.4)}`,
            borderRadius: theme.radius.md,
            py: 0.6,
            px: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontSize: 38,
              fontWeight: 800,
              color,
              letterSpacing: '-0.02em',
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {value}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onIncrement}
          disabled={disabled}
          aria-label="increment score"
          sx={{
            width: 32,
            height: 32,
            backgroundColor: withAlpha(theme.actionColors.made, 0.18),
            border: `1px solid ${withAlpha(theme.actionColors.made, 0.4)}`,
            color: theme.actionColors.made,
            '&:hover': { backgroundColor: withAlpha(theme.actionColors.made, 0.3) },
            '&.Mui-disabled': { opacity: 0.3 },
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  );
}

export default function ScorePanel({
  teamName,
  opponentName,
  localScore = 0,
  opponentScore = 0,
  onLocalIncrement,
  onLocalDecrement,
  onOpponentIncrement,
  onOpponentDecrement,
  disabled = false,
  showOpponentQuickButtons = true,
  onOpponentQuickScore,
}) {
  const theme = useStatsTheme();
  const localColor = theme.accent;
  const opponentColor = theme.actionColors.opponent;

  return (
    <Box
      sx={{
        backgroundColor: theme.surfaces.elevated,
        borderRadius: `${theme.radius.lg}px`,
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        boxShadow: theme.shadows.md,
        border: `1px solid ${theme.surfaces.border}`,
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <ScoreBlock
          label={teamName || 'Mi equipo'}
          value={localScore}
          color={localColor}
          onIncrement={onLocalIncrement}
          onDecrement={onLocalDecrement}
          disabled={disabled}
        />
        <Box sx={{ width: 1, backgroundColor: theme.surfaces.divider }} />
        <ScoreBlock
          label={opponentName || 'Oponente'}
          value={opponentScore}
          color={opponentColor}
          onIncrement={onOpponentIncrement}
          onDecrement={onOpponentDecrement}
          disabled={disabled}
        />
      </Box>

      {showOpponentQuickButtons && (
        <Box sx={{ display: 'flex', gap: 0.6, justifyContent: 'flex-end' }}>
          {[
            { code: 'OPP_FT', label: '+1', pts: 1 },
            { code: 'OPP_2PTS', label: '+2', pts: 2 },
            { code: 'OPP_3PTS', label: '+3', pts: 3 },
          ].map(({ code, label, pts }) => (
            <ButtonBase
              key={code}
              onClick={() => typeof onOpponentQuickScore === 'function' && onOpponentQuickScore(code, pts)}
              aria-label={`Opponent ${label}`}
              sx={{
                px: 1.2,
                height: 28,
                borderRadius: theme.radius.pill,
                backgroundColor: withAlpha(opponentColor, 0.12),
                border: `1px solid ${withAlpha(opponentColor, 0.36)}`,
                color: theme.text.primary,
                fontFamily: theme.typography.fontFamily,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.04em',
                '&:hover': { backgroundColor: withAlpha(opponentColor, 0.24) },
              }}
            >
              {label}
            </ButtonBase>
          ))}
        </Box>
      )}
    </Box>
  );
}
